import React from 'react';
import { ModelConfiguration, ModelType } from './types';
import ModelCard from './ModelCard';
import { useAdminTranslation } from '../../../modules/i18n/hooks/useTranslation';

interface ModelPairViewProps {
  config: ModelConfiguration;
  onOpenSettings: (type: ModelType) => void;
}

const ModelPairView: React.FC<ModelPairViewProps> = ({ config, onOpenSettings }) => {
  const { t } = useAdminTranslation();
  const { currentSettings, fallbackSettings } = config;

  return (
    <div className="pb-6">
      {/* Model Cards Container - Side by Side */}
      <div>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Primary Model Column */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-600 dark:bg-blue-500" />
              <span className="text-xs font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide">
                {t('models.primaryModel')}
              </span>
            </div>
            <ModelCard
              modelType={config.type}
              settings={currentSettings}
              role="primary"
              onConfigure={() => onOpenSettings(config.type)}
            />
          </div>

          {/* Fallback Model Column */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1.5 w-1.5 rounded-full bg-orange-500 dark:bg-orange-400" />
              <span className="text-xs font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide">
                {t('models.fallbackModel')}
              </span>
            </div>
            {fallbackSettings ? (
              <ModelCard
                modelType={config.type}
                settings={fallbackSettings}
                role="fallback"
                onConfigure={() => onOpenSettings(config.type)}
              />
            ) : (
              <div className="h-full min-h-[240px] rounded-lg border-2 border-dashed border-gray-300 dark:border-border-dark bg-gray-50 dark:bg-dark-background-secondary/30 p-6 flex items-center justify-center">
                <div className="text-center max-w-xs">
                  <svg
                    className="mx-auto h-10 w-10 text-gray-400 dark:text-dark-text-secondary mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary mb-1">
                    {t('models.noFallback')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fallback Info Box */}
        {fallbackSettings && (
          <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-4 dark:border-primary/30 dark:bg-primary/10">
            <div className="flex items-start gap-3">
              <svg
                className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-primary mb-1">
                  {t('models.fallbackInfo.title')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                  {t('models.fallbackDescription')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelPairView;